import type { ReactNode } from "react";
import { memo, useMemo } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { ResponsiveFrameProps } from "@circle-react-shared/ResponsiveFrame";
import { ResponsiveFrame } from "@circle-react-shared/ResponsiveFrame";
import builderTailwindConfig from "../styles/tailwind.builder.config";
import { IFRAME_DATA_TEST_ID, IFRAME_ID } from "./constants";
import { useDynamicGoogleFonts } from "./useDynamicGoogleFonts";
import { useScrollToBlock } from "./useScrollToBlock";
import {
  type InitialContent,
  getInitialContent,
  mountPointSelector,
} from "./utils";

export interface TailwindFrameWithBottomPaddingProps
  extends ResponsiveFrameProps,
    InitialContent {
  children: ReactNode;
}

export const TailwindFrameWithBottomPadding = memo(
  ({
    children,
    className,
    headStart,
    headEnd,
    bodyStart,
    bodyEnd,
    tailwindConfig = builderTailwindConfig,
    ...rest
  }: TailwindFrameWithBottomPaddingProps) => {
    const { isDragging } = useBuilderStore(state => ({
      isDragging: state.isDragging,
    }));

    useScrollToBlock();
    useDynamicGoogleFonts();

    const initialContent = useMemo(
      () =>
        getInitialContent({
          headStart: headStart || "",
          headEnd: headEnd || "",
          bodyStart: bodyStart || "",
          bodyEnd: bodyEnd || "",
          tailwindConfig,
        }),
      [headStart, headEnd, bodyStart, bodyEnd, tailwindConfig],
    );

    return (
      <>
        <ResponsiveFrame
          id={IFRAME_ID}
          initialContent={initialContent}
          mountTarget={mountPointSelector}
          shouldAdaptHeightToContentSize
          data-testid={IFRAME_DATA_TEST_ID}
          className={classNames(className, {
            // https://github.com/clauderic/dnd-kit/issues/1374
            ["pointer-events-none"]: isDragging,
          })}
          style={{ position: "relative" }}
          {...rest}
        >
          {children}
        </ResponsiveFrame>
        {/* since we're calculating the height of the iframe, we need to add a spacer at the bottom of the iframe to give the toolbar breathing room */}
        <div className="h-32 w-full bg-transparent" />
      </>
    );
  },
);
TailwindFrameWithBottomPadding.displayName =
  "BuilderUITailwindFrameWithBottomPadding";
